'use client';

import React, { useMemo, useRef } from 'react';

import { useStoreRedirect } from '@/hooks/useStoreRedirect';
import { IMediaItem, MediaType } from '@/lib/types';
import { cn } from '@/lib/utils';

import { ImageItem } from './Image';
import { VideoItem } from './Video';
import { useIsMobileDevice } from '@/hooks/useIsMobileDevice';

const mapMediaByType: Record<MediaType, any> = {
  image: ImageItem,
  video: VideoItem,
};

interface MediaItemProps extends IMediaItem {
  uniqueIdentifier: string;
  className?: string;
  itemIndex?: number;
  autoplay?: boolean;
  onBlockClick?: () => void;
}

export const MediaItem: React.FC<MediaItemProps> = ({
  type,
  url,
  mobileUrl,
  redirectUrl,
  className,
  autoplay,
  onBlockClick,
}) => {
  const itemRef = useRef<HTMLDivElement>(null);
  const { redirect } = useStoreRedirect();
  const isMobile = useIsMobileDevice();

  const urlToUse = useMemo(() => {
    return isMobile && mobileUrl ? mobileUrl : url;
  }, [url, mobileUrl, isMobile]);

  const handleImageClick = async () => {
    onBlockClick?.();
    await redirect(redirectUrl, false);
  };

  let onClick = onBlockClick;
  if (type === 'image') {
    onClick = redirectUrl ? handleImageClick : undefined;
  }

  return (
    <div
      className={cn(
        'mediaItem flex gap-y-2.5 rounded-[20px] border-none bg-black/25 p-5 lg:flex-row lg:items-center lg:gap-x-10',
        type === 'image' &&
          'shrink-1 grow-0 basis-auto justify-center self-center hover:opacity-90',
        type === 'video' && 'w-full max-w-[640px]',
        className
      )}
      ref={itemRef}>
      {mapMediaByType[type]({ assetUrl: urlToUse, autoplay, onClick })}
    </div>
  );
};
